import { useEffect, useState } from 'react';
import type { BillingSummary, DeviceRole, FrameStanding } from '@mantelpiece/shared';
import { api, type StoredSession } from '../lib/api';
import { go } from '../lib/route';
import { daysUntil, longDate } from '../lib/standing';

/** A trial with this long left, or less, gets a line on the frames list. */
const NUDGE_DAYS = 5;

/**
 * One line at the top of the frames list about the money, when there is
 * something to say: a trial in its last few days, or frames that have gone
 * read-only or dark. Says nothing at all in the ordinary case, and nothing on
 * a deployment that does not charge.
 */
export const BillingBanner = ({ session }: { readonly session: StoredSession }) => {
  const [summary, setSummary] = useState<BillingSummary | null>(null);

  useEffect(() => {
    let live = true;
    api
      .billing(session.token)
      .then((result) => live && setSummary(result))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [session.token]);

  if (!summary || !summary.enabled) return null;

  const link = (
    <button type="button" className="linky" onClick={() => go('/billing')}>
      Open billing
    </button>
  );

  if (summary.standing === 'dormant') {
    return (
      <p className="alert">
        Your frames are off their screens: the subscription has lapsed and the grace period has
        run out. Nothing has been deleted — subscribe and they come straight back. {link}
      </p>
    );
  }

  if (summary.standing === 'grace') {
    return (
      <p className="alert">
        {summary.subscription
          ? 'Your subscription has lapsed.'
          : 'Your trial has ended.'}{' '}
        Your frames keep showing what is on them
        {summary.standingUntil ? ` until ${longDate(summary.standingUntil)}` : ''}, but nobody can
        send anything new. {link}
      </p>
    );
  }

  if (!summary.subscription && summary.standingUntil !== null) {
    const days = daysUntil(summary.standingUntil);
    if (days > NUDGE_DAYS) return null;
    return (
      <p className="alert" data-tone="ok">
        {days === 0
          ? 'Your trial ends today.'
          : `Your trial ends in ${days} day${days === 1 ? '' : 's'}.`}{' '}
        After that your frame keeps showing but stops taking photos. {link}
      </p>
    );
  }

  return null;
};

interface NoticeProps {
  readonly standing: FrameStanding;
  readonly role: DeviceRole;
  readonly ownerName: string;
}

/**
 * The same fact from inside a frame, for whoever is looking at it. The owner
 * is pointed at billing; everyone else is told whose frame it is, since they
 * cannot fix it and should not be left wondering why the Add tab has gone.
 */
export const StandingNotice = ({ standing, role, ownerName }: NoticeProps) => {
  if (standing === 'live') return null;
  const dark = standing === 'dormant';

  if (role === 'owner') {
    return (
      <p className="alert">
        {dark
          ? 'This frame is off its screens because your subscription has lapsed. Subscribe and it comes back with everything still on it.'
          : 'This frame is read-only: nobody can send to it until you subscribe. It keeps showing what it has meanwhile.'}{' '}
        <button type="button" className="linky" onClick={() => go('/billing')}>
          Open billing
        </button>
      </p>
    );
  }

  return (
    <p className="alert">
      {dark
        ? `This frame is off its screens for now. ${ownerName} owns it and can bring it back.`
        : `This frame is not taking new photos for now. ${ownerName} owns it and can sort that out.`}
    </p>
  );
};
